import type {
  ActiveManualOverride,
  ManualOverrideReleasePolicy,
  PendingManualOverrideAction,
} from '@/services/inventory'
import { formatOverrideFieldDisplayValue } from '@/utils/overrideFieldDisplay'
import { normalizeEpochSeconds, normalizeIntegerInput, toDateInputValue } from '@/utils/vehicleFieldValueNormalization'
import { isVehicleHistoryBooleanField, normalizeBooleanLike } from '@/utils/vehicleHistoryBooleanFields'
import { isModelEField } from '@/utils/editVehicleBooleanFields'

export type ReleasePreviewStatus = 'resolved' | 'empty' | 'unavailable'

const SHORT_POLICY_LABELS: Record<ManualOverrideReleasePolicy, string> = {
  UNTIL_FEED_VALUE_CHANGES: 'Until feed value changes',
  UNTIL_SOLD: 'Until sold',
  MANUAL_RELEASE_ONLY: 'Manual release only',
  UNTIL_DATE: 'Until date',
}

export type FieldOverrideStateInput = {
  field: string
  currentValue: unknown
  localValue: unknown
  loadedEffectiveValue: unknown
  activeOverride?: ActiveManualOverride | null
  pendingAction?: PendingManualOverrideAction | null
  provenanceMissing?: boolean
  allowMissingProvenance?: boolean
  removalStaged?: boolean
  conflictMessage?: string | null
  releasePolicy?: ManualOverrideReleasePolicy
  expiresAt?: string
  loadedReleasePolicy?: ManualOverrideReleasePolicy
  loadedExpiresAt?: string
  dealerTimeZone?: string
}

export type FieldOverrideState = {
  field: string
  currentValue: unknown
  activeValue: unknown | undefined
  pendingValue: unknown | undefined
  mappedValueAtLock: unknown | undefined
  valueIfReleasedNow: unknown | undefined
  releasePreviewStatus: ReleasePreviewStatus
  localValue: unknown
  effectiveInputValue: unknown
  loadedEffectiveValue: unknown
  hasUnsavedLocalEdit: boolean
  hasPendingDraftAction: boolean
  hasPendingPublish: boolean
  pendingIsRelease: boolean
  hasActiveOverride: boolean
  hasConflict: boolean
  provenanceMissing: boolean
  provenanceBlocked: boolean
  overrideBlocked: boolean
  removalStaged: boolean
  isOverrideEligible: boolean
  hasUnsavedPolicyChange: boolean
  hasUnsavedLocalChange: boolean
  displaySummaryText: string
  valueChainText: string
  statusLabel: string | null
  policyText: string | null
  releasePolicyLabel: string | null
  expiresAt: string | undefined
  sourceFeed: string | undefined
  sourceField: string | undefined
  sourcePath: string | undefined
  currentMappingSourceFeed: string | undefined
  currentMappingSourceField: string | undefined
  currentMappingSourcePath: string | undefined
  lockedBy: string | undefined
  lockedAt: string | undefined
  showDiscard: boolean
  showRelease: boolean
  showSettings: boolean
}

export function valuesEqual(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b)
}

/**
 * dealer_photos compare key: order-sensitive, case-insensitive (user decision), and independent of
 * any display formatting. Non-array / blank entries are ignored; no usable photos is 'empty'.
 */
function normalizeDealerPhotosForCompare(value: unknown): string {
  const urls = Array.isArray(value)
    ? value
        .filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
        .map((item) => item.trim().toLowerCase())
    : []
  return urls.length === 0 ? 'empty' : JSON.stringify(urls)
}

function normalizeFieldValueForCompare(field: string, value: unknown): string {
  if (field === 'dealer_photos') return normalizeDealerPhotosForCompare(value)
  if (value == null) return 'empty'
  if (typeof value === 'string' && value.trim() === '') return 'empty'

  if (field === 'date_in_stock_ts') {
    const seconds = normalizeEpochSeconds(value)
    return seconds === undefined ? 'empty' : String(seconds)
  }

  if (field === 'passenger_capacity') {
    const normalized = normalizeIntegerInput(value)
    return normalized === null ? 'empty' : String(normalized)
  }

  if (isVehicleHistoryBooleanField(field) || isModelEField(field)) {
    const normalized = normalizeBooleanLike(value)
    return normalized === undefined ? 'empty' : String(normalized)
  }

  return formatOverrideFieldDisplayValue(field, value).trim().toLowerCase()
}

/** Compact summary for photo lists so value chains never print raw URL arrays. */
function formatDealerPhotosSummary(value: unknown): string {
  const count = Array.isArray(value)
    ? value.filter((item) => typeof item === 'string' && item.trim().length > 0).length
    : 0
  if (count === 0) return 'No photos'
  return `${count} photo${count === 1 ? '' : 's'}`
}

/** Field-aware display formatting for value chains (photo lists are summarised). */
function formatFieldOverrideValue(
  field: string,
  value: unknown,
  options?: { dealerTimeZone?: string },
): string {
  if (field === 'dealer_photos') return formatDealerPhotosSummary(value)
  return formatOverrideFieldDisplayValue(field, value, options)
}

export function areFieldValuesEquivalent(field: string, a: unknown, b: unknown): boolean {
  return normalizeFieldValueForCompare(field, a) === normalizeFieldValueForCompare(field, b)
}

/** Resolves feed/base mapped value (never pending manualValue or active overlay). */
export function resolveCurrentBaseValue(input: {
  rawVehicleValue?: unknown
  activeOverride?: ActiveManualOverride | null
  pendingAction?: PendingManualOverrideAction | null
}): unknown {
  const pending = input.pendingAction
  if (pending && pending.action !== 'REMOVE_OVERRIDE' && 'mappedValueAtLock' in pending) {
    return pending.mappedValueAtLock
  }
  if (input.activeOverride && 'valueIfReleasedNow' in input.activeOverride) {
    return input.activeOverride.valueIfReleasedNow
  }
  if (input.activeOverride && 'currentFeedValue' in input.activeOverride) {
    return input.activeOverride.currentFeedValue
  }
  if (input.activeOverride) {
    return undefined
  }
  return input.rawVehicleValue
}

/** Input shows pending manualValue, else active manualValue, else current/base. */
export function resolveEffectiveInputValue(input: {
  currentValue: unknown
  activeOverride?: ActiveManualOverride | null
  pendingAction?: PendingManualOverrideAction | null
  removalStaged?: boolean
}): unknown {
  const pending = input.pendingAction
  if (pending && pending.action !== 'REMOVE_OVERRIDE' && 'manualValue' in pending) {
    return pending.manualValue
  }
  if (input.activeOverride && !input.removalStaged) {
    return input.activeOverride.manualValue
  }
  return input.currentValue
}

const joinParts = (...parts: Array<string | null | undefined>): string =>
  parts.filter((part) => Boolean(String(part || '').trim())).join(' · ')

function normalizeExpiresAtForCompare(value: string | undefined): string {
  const raw = String(value ?? '').trim()
  if (!raw) return ''
  const date = new Date(raw)
  if (Number.isNaN(date.getTime())) return raw
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function isBlankString(value: unknown): boolean {
  return typeof value === 'string' && value.trim() === ''
}

function resolveReleasePreviewStatus(
  status: ReleasePreviewStatus | undefined,
  valueIfReleasedNow: unknown | undefined,
): ReleasePreviewStatus {
  if (status) return status
  if (valueIfReleasedNow === undefined) return 'unavailable'
  if (valueIfReleasedNow === null || isBlankString(valueIfReleasedNow)) return 'empty'
  return 'resolved'
}

function formatReleasePreviewTarget(
  field: string,
  status: ReleasePreviewStatus,
  valueIfReleasedNow: unknown | undefined,
  dealerTimeZone?: string,
): string {
  if (status === 'unavailable') return 'latest mapped feed value'
  if (status === 'empty') return 'empty'
  return formatFieldOverrideValue(field, valueIfReleasedNow, { dealerTimeZone })
}

function formatFeedValueLabel(
  field: string,
  value: unknown,
  dealerTimeZone?: string,
): string {
  if (value === null || value === undefined) return 'No feed value'
  return formatFieldOverrideValue(field, value, { dealerTimeZone })
}

export function buildFieldOverrideState(input: FieldOverrideStateInput): FieldOverrideState {
  const pending = input.pendingAction
  const active = input.activeOverride ?? null
  const pendingIsRelease = pending?.action === 'REMOVE_OVERRIDE'
  const hasPendingDraftAction = Boolean(pending)
  const hasPendingPublish = Boolean(
    pending && !pendingIsRelease && (pending.action === 'ADD_OVERRIDE' || pending.action === 'UPDATE_OVERRIDE'),
  )
  const hasActiveOverride = Boolean(active) && !input.removalStaged
  const activeValue = active?.manualValue
  const pendingValue = hasPendingPublish ? pending?.manualValue : undefined
  const valueIfReleasedNow =
    active && 'valueIfReleasedNow' in active
      ? active.valueIfReleasedNow
      : active && 'currentFeedValue' in active
        ? active.currentFeedValue
        : undefined
  const releasePreviewStatus = resolveReleasePreviewStatus(
    active?.releasePreviewStatus,
    valueIfReleasedNow,
  )

  const effectiveInputValue = resolveEffectiveInputValue({
    currentValue: input.currentValue,
    activeOverride: active,
    pendingAction: pending,
    removalStaged: input.removalStaged,
  })

  const loadedEffectiveValue = input.loadedEffectiveValue
  const hasUnsavedLocalEdit =
    input.field === 'dealer_photos'
      ? !areFieldValuesEquivalent(input.field, input.localValue, loadedEffectiveValue)
      : !valuesEqual(input.localValue, loadedEffectiveValue)
  const provenanceBlocked =
    Boolean(input.provenanceMissing) &&
    !input.allowMissingProvenance &&
    !active &&
    !hasPendingDraftAction
  const overrideBlocked = provenanceBlocked
  const hasConflict = Boolean(input.conflictMessage)

  const fmt = (value: unknown) =>
    formatFieldOverrideValue(input.field, value, {
      dealerTimeZone: input.dealerTimeZone,
    })
  const isEmptyFeedValue = (value: unknown): boolean =>
    value === null || (typeof value === 'string' && value.trim() === '')
  let valueChainText = ''
  let statusLabel: string | null = null
  let isUnsavedNewOverrideFromEmpty = false

  if (!overrideBlocked) {
    const chain: string[] = []

    if (pendingIsRelease) {
      chain.push(`Feed now: ${formatFeedValueLabel(input.field, input.currentValue, input.dealerTimeZone)}`)
      if (activeValue !== undefined) chain.push(`Current: ${fmt(activeValue)}`)
      statusLabel = 'Pending release'
    } else if (input.removalStaged) {
      chain.push(`Will return to feed value: ${formatFeedValueLabel(input.field, input.currentValue, input.dealerTimeZone)}`)
      if (activeValue !== undefined) chain.push(`Current: ${fmt(activeValue)}`)
      statusLabel = 'Release pending'
    } else if (hasUnsavedLocalEdit) {
      if (hasActiveOverride && activeValue !== undefined) {
        chain.push(`Current: ${fmt(activeValue)}`)
        chain.push(`Editing: ${fmt(input.localValue)}`)
      } else if (isEmptyFeedValue(input.currentValue)) {
        chain.push('No feed value')
        chain.push(`Current: ${fmt(input.localValue)}`)
        isUnsavedNewOverrideFromEmpty = true
      } else {
        chain.push(`Current: ${formatFeedValueLabel(input.field, input.currentValue, input.dealerTimeZone)}`)
        chain.push(`Editing: ${fmt(input.localValue)}`)
      }
      statusLabel = 'Not saved'
    } else if (hasPendingPublish && pendingValue !== undefined) {
      chain.push(`Current: ${formatFeedValueLabel(input.field, input.currentValue, input.dealerTimeZone)}`)
      chain.push(`Pending publish: ${fmt(pendingValue)}`)
      statusLabel = 'Pending publish'
    } else if (hasActiveOverride && activeValue !== undefined) {
      chain.push(
        `Feed now: ${formatReleasePreviewTarget(
          input.field,
          releasePreviewStatus,
          valueIfReleasedNow,
          input.dealerTimeZone,
        )}`,
      )
      chain.push(`Current: ${fmt(activeValue)}`)
      statusLabel = 'Locked override'
    } else {
      statusLabel = 'Override eligible'
    }

    valueChainText = chain.join(' → ')
  }

  const isOverrideEligible =
    !overrideBlocked &&
    !hasActiveOverride &&
    !hasUnsavedLocalEdit &&
    !hasPendingPublish &&
    !pendingIsRelease &&
    !input.removalStaged &&
    !isUnsavedNewOverrideFromEmpty

  const loadedReleasePolicy =
    input.loadedReleasePolicy ?? active?.releasePolicy ?? 'UNTIL_FEED_VALUE_CHANGES'
  const currentReleasePolicy = input.releasePolicy ?? 'UNTIL_FEED_VALUE_CHANGES'
  const loadedExpiresAt = normalizeExpiresAtForCompare(
    input.loadedExpiresAt ?? active?.expiresAt,
  )
  const currentExpiresAt = normalizeExpiresAtForCompare(input.expiresAt)
  const hasUnsavedPolicyChange =
    hasActiveOverride &&
    !input.removalStaged &&
    !overrideBlocked &&
    (currentReleasePolicy !== loadedReleasePolicy || currentExpiresAt !== loadedExpiresAt)

  const hasUnsavedLocalChange =
    !overrideBlocked &&
    (hasUnsavedLocalEdit ||
      hasUnsavedPolicyChange ||
      Boolean(input.removalStaged))

  const policyKey = input.releasePolicy ?? 'UNTIL_FEED_VALUE_CHANGES'
  const shortPolicy = SHORT_POLICY_LABELS[policyKey] ?? policyKey
  const policyText = `Policy: ${shortPolicy}`

  let displaySummaryText = ''
  if (provenanceBlocked) {
    displaySummaryText = 'Cannot override · Missing feed mapping'
  } else if (input.removalStaged) {
    displaySummaryText = joinParts('Release pending', valueChainText)
  } else if (hasActiveOverride && !hasUnsavedLocalEdit && !hasPendingPublish && !pendingIsRelease) {
    displaySummaryText = joinParts('Locked override', valueChainText)
  } else if (isUnsavedNewOverrideFromEmpty) {
    displaySummaryText = joinParts('Unsaved override', valueChainText)
  } else if (isOverrideEligible) {
    displaySummaryText =
      input.provenanceMissing || input.currentValue === null || input.currentValue === undefined
        ? 'No feed value'
        : 'Override eligible'
  } else {
    displaySummaryText = joinParts(valueChainText, hasUnsavedLocalEdit ? 'Not saved' : null)
  }

  return {
    field: input.field,
    currentValue: input.currentValue,
    activeValue,
    pendingValue,
    mappedValueAtLock: active?.mappedValueAtLock,
    valueIfReleasedNow,
    releasePreviewStatus,
    localValue: input.localValue,
    effectiveInputValue,
    loadedEffectiveValue,
    hasUnsavedLocalEdit,
    hasPendingDraftAction,
    hasPendingPublish,
    pendingIsRelease,
    hasActiveOverride,
    hasConflict,
    provenanceMissing: Boolean(input.provenanceMissing),
    provenanceBlocked,
    overrideBlocked,
    removalStaged: Boolean(input.removalStaged),
    isOverrideEligible,
    hasUnsavedPolicyChange,
    hasUnsavedLocalChange,
    displaySummaryText,
    valueChainText,
    statusLabel,
    policyText,
    releasePolicyLabel: shortPolicy,
    expiresAt: active?.expiresAt,
    sourceFeed: active?.sourceFeed,
    sourceField: active?.sourceField,
    sourcePath: active?.sourcePath,
    currentMappingSourceFeed: active?.currentMappingSourceFeed,
    currentMappingSourceField: active?.currentMappingSourceField,
    currentMappingSourcePath: active?.currentMappingSourcePath,
    lockedBy: active?.lockedBy,
    lockedAt: active?.lockedAt,
    showDiscard: hasPendingDraftAction && !pendingIsRelease,
    showRelease: Boolean(active) && !input.removalStaged,
    showSettings: !overrideBlocked,
  }
}
